<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4 pb-3 border-bottom border-subtle d-flex flex-column flex-md-row justify-content-between align-items-md-center">
      <div>
        <h3 class="text-white font-weight-bold mb-1">
          <i class="mdi mdi-trophy text-warning mr-2"></i>{{ $t('quests.title') }}
        </h3>
        <p class="text-muted small mb-0">{{ $t('quests.subtitle') }}</p>
      </div>
      <div class="mt-3 mt-md-0">
        <router-link
          :to="{ name: 'CreateReward' }"
          class="btn btn-primary btn-sm px-4 font-weight-bold d-inline-flex align-items-center"
        >
          <i class="mdi mdi-plus-circle mr-1"></i>
          {{ $t('quests.createNewQuest') }}
        </router-link>
      </div>
    </div>

    <div class="col-12 grid-margin">
      <div class="card shadow-sm">
        <div class="card-body p-4">
          <!-- Search & Filter Bar -->
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-2">
            <!-- Status Filter Tabs -->
            <div class="btn-group btn-group-sm mb-2 mb-md-0" role="group">
              <button
                type="button"
                class="btn font-weight-bold"
                :class="statusFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'"
                @click="statusFilter = 'all'"
              >
                {{ $t('common.all') }} ({{ reward.length }})
              </button>
              <button
                type="button"
                class="btn font-weight-bold"
                :class="statusFilter === 'published' ? 'btn-success' : 'btn-outline-secondary'"
                @click="statusFilter = 'published'"
              >
                <i class="mdi mdi-check-circle mr-1"></i> {{ $t('quests.statusActive') }} ({{ countByStatus('published') }})
              </button>
              <button
                type="button"
                class="btn font-weight-bold"
                :class="statusFilter === 'draft' ? 'btn-warning text-dark' : 'btn-outline-secondary'"
                @click="statusFilter = 'draft'"
              >
                <i class="mdi mdi-pause-circle mr-1"></i> {{ $t('quests.statusDraftPaused') }} ({{ countByStatus('draft') }})
              </button>
            </div>

            <div class="search-box" style="max-width: 320px; width: 100%;">
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-input border-card text-muted">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('quests.searchPlaceholder')"
                />
                <button
                  v-if="search"
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="search = ''"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover align-middle" style="table-layout: fixed; width: 100%;">
              <thead>
                <tr class="text-muted small text-uppercase">
                  <th style="width: 40px;">No</th>
                  <th style="width: 260px;">{{ $t('quests.questName') }}</th>
                  <th style="width: 120px;">{{ $t('quests.division') }}</th>
                  <th style="width: 160px;">{{ $t('quests.reward') }}</th>
                  <th style="width: 130px;">Status / {{ $t('quests.frequency') }}</th>
                  <th class="text-right" style="width: 135px; padding-right: 1rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, index) in paginatedReward" :key="data.id">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="overflow-hidden">
                      <router-link
                        :to="{ name: 'DetailReward', params: { id: String(data.id) } }"
                        class="text-white font-weight-bold text-decoration-none d-block text-truncate"
                        :title="data.name"
                      >
                        <i class="mdi mdi-flag-checkered text-warning mr-1"></i>
                        {{ data.name }}
                      </router-link>
                      <div class="text-muted small text-truncate" v-if="data.desc || data.description" :title="data.desc || data.description">
                        {{ data.desc || data.description }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-outline-info font-weight-medium text-truncate d-inline-block" style="max-width: 110px;" :title="resolveDivisionName(data)">
                      {{ resolveDivisionName(data) }}
                    </span>
                  </td>
                  <td>
                    <span v-if="data.gold" class="badge badge-warning text-dark font-weight-bold mr-1">
                      <i class="mdi mdi-circle-multiple mr-1" aria-hidden="true"></i>{{ Number(data.gold).toLocaleString($currentLocale === 'id' ? 'id-ID' : 'en-US') }} {{ $t('common.gold') }}
                    </span>
                    <span v-if="data.non_gold" class="badge badge-outline-success font-weight-medium">
                      <i class="mdi mdi-gift-outline mr-1" aria-hidden="true"></i>{{ data.non_gold }}
                    </span>
                    <span v-if="!data.gold && !data.non_gold" class="text-muted small">
                      0 {{ $t('common.gold') }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex flex-column gap-1">
                      <button 
                        type="button" 
                        class="btn btn-xs py-0 px-2 rounded-pill font-weight-bold border-0 text-left"
                        :style="data.status === 'published' ? 'background: rgba(16, 185, 129, 0.2); color: #34d399;' : 'background: rgba(100, 116, 139, 0.2); color: #94a3b8;'"
                        @click="handleToggleStatus(data)"
                        :title="($currentLocale === 'id' ? 'Klik untuk mengubah status (sekarang: ' : 'Click to toggle status (current: ') + (data.status || 'published') + ')'"
                      >
                        <i :class="data.status === 'published' ? 'mdi mdi-check-circle mr-1' : 'mdi mdi-pause-circle mr-1'"></i>
                        {{ data.status === 'published' ? ($currentLocale === 'id' ? 'Aktif' : 'Active') : ($currentLocale === 'id' ? 'Draft / Jeda' : 'Draft / Paused') }}
                      </button>
                      <span class="text-muted text-uppercase" style="font-size: 10px;">
                        {{ resolveFrequencyLabel(data) }}
                      </span>
                    </div>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <router-link
                        :to="{ name: 'DetailReward', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-info"
                        :title="$t('quests.viewDetails')"
                      >
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                      <router-link
                        :to="{ name: 'EditReward', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-warning"
                        :title="$t('quests.editQuest')"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </router-link>
                      <button
                        type="button"
                        class="btn btn-action-icon btn-outline-primary"
                        @click="handleClone(data)"
                        :title="$currentLocale === 'id' ? 'Duplikat Misi' : 'Clone Quest'"
                      >
                        <i class="mdi mdi-content-copy"></i>
                      </button>
                      <button
                        type="button"
                        class="btn btn-action-icon btn-outline-danger"
                        @click="handleDelete(data.id)"
                        :title="$t('quests.deleteQuest')"
                      >
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedReward.length === 0">
                  <td colspan="6" class="text-center py-5">
                    <EmptyState
                      icon="mdi-gift-outline"
                      :title="$t('quests.emptyTitle')"
                      :description="$t('quests.emptyDesc')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="filteredReward.length > itemsPerPage" class="mt-3">
            <PaginationComponents
              :totalItems="filteredReward.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getReward, deleteReward, cloneReward, toggleRewardStatus } from "../../services/rewardService";
import PaginationComponents from "../../components/PaginationComponents.vue";
import EmptyState from "../../components/EmptyState.vue";
import Swal from 'sweetalert2';

export default {
  name: "RewardList",
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: "",
      statusFilter: "all",
      reward: [],
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,
    };
  },
  computed: {
    filteredReward() {
      return this.reward.filter((item) => {
        if (this.statusFilter === 'published' && (item.status || 'published') !== 'published') return false;
        if (this.statusFilter === 'draft' && item.status !== 'draft') return false;
        if (!this.search) return true;
        const q = this.search.toLowerCase();
        const nameMatch = (item.name || "").toLowerCase().includes(q);
        const descMatch = (item.desc || item.description || "").toLowerCase().includes(q);
        const divMatch = (item.division?.name || item.squad?.name || "").toLowerCase().includes(q);
        return nameMatch || descMatch || divMatch;
      });
    },
    paginatedReward() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredReward.slice(start, end);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
    statusFilter() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.fetchRewards();
  },
  methods: {
    resolveFrequencyLabel(data) {
      const isId = this.$currentLocale === 'id';
      const repeatType = (data?.repeatable_rule?.repeat_type || data?.frequency || '').toLowerCase();
      if (repeatType === 'daily') return isId ? 'Harian' : 'Daily';
      if (repeatType === 'weekly') return isId ? 'Mingguan' : 'Weekly';
      if (repeatType === 'monthly') return isId ? 'Bulanan' : 'Monthly';
      if (data?.type === 'onetime' || (!data?.repeatable_rule && data?.type !== 'always_active')) {
        return isId ? 'Satu Kali' : 'One-Time';
      }
      return isId ? 'Rutin' : 'Routine';
    },
    resolveDivisionName(data) {
      if (!data) return this.$currentLocale === 'id' ? 'Semua Divisi' : 'All Divisions';
      if (data.division && data.division.name) return data.division.name;
      if (data.squad && data.squad.name) return data.squad.name;
      if (Array.isArray(data.assignees)) {
        const divNames = data.assignees
          .map(a => a && a.division && a.division.name)
          .filter(Boolean);
        if (divNames.length > 0) return divNames.join(', ');
      }
      return this.$currentLocale === 'id' ? 'Semua Divisi' : 'All Divisions';
    },
    countByStatus(status) {
      if (status === 'published') {
        return this.reward.filter(r => (r.status || 'published') === 'published').length;
      }
      return this.reward.filter(r => r.status === 'draft').length;
    },
    async fetchRewards() {
      this.loading = true;
      try {
        const response = await getReward();
        this.reward = response.data || [];
      } catch (e) {
        // error handled
      } finally {
        this.loading = false;
      }
    },
    async handleClone(data) {
      try {
        await cloneReward(data.id);
        Swal.fire({
          icon: 'success',
          title: this.$t('quests.cloneSuccess') || (this.$currentLocale === 'id' ? 'Misi Berhasil Disalin!' : 'Quest Cloned Successfully!'),
          text: (this.$t('quests.cloneSuccessDesc') || (this.$currentLocale === 'id' ? 'Salinan "{name}" telah dibuat dan aktif.' : 'A copy of "{name}" has been created and is active.')).replace('{name}', data.name || ''),
          timer: 2000,
          showConfirmButton: false,
        });
        await this.fetchRewards();
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: this.$t('quests.cloneFailed') || (this.$currentLocale === 'id' ? 'Gagal Duplikasi' : 'Failed to Clone Quest'),
          text: err.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal menyalin misi.' : 'Failed to clone quest.'),
        });
      }
    },
    async handleToggleStatus(data) {
      try {
        const res = await toggleRewardStatus(data.id);
        const newStatus = res.data?.status || (data.status === 'published' ? 'draft' : 'published');
        data.status = newStatus;
        const statusLabel = newStatus === 'published' ? (this.$currentLocale === 'id' ? 'Aktif' : 'Active') : (this.$currentLocale === 'id' ? 'Draft / Jeda' : 'Draft / Paused');
        Swal.fire({
          icon: 'info',
          title: this.$t('quests.statusUpdated') || (this.$currentLocale === 'id' ? 'Status Diperbarui' : 'Status Updated'),
          text: (this.$t('quests.statusUpdatedDesc') || (this.$currentLocale === 'id' ? 'Misi "{name}" kini {status}.' : 'Quest "{name}" is now {status}.')).replace('{name}', data.name || '').replace('{status}', statusLabel),
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: this.$t('quests.statusUpdateFailed') || (this.$currentLocale === 'id' ? 'Gagal Mengubah Status' : 'Failed to Update Status'),
          text: err.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal memperbarui status misi.' : 'Failed to update quest status.'),
        });
      }
    },
    async handleDelete(id) {
      const result = await Swal.fire({
        title: this.$t("quests.confirmDelete") || (this.$currentLocale === 'id' ? 'Hapus Misi?' : 'Delete Quest?'),
        text: this.$t("quests.deleteConfirmText") || (this.$currentLocale === 'id' ? 'Misi ini akan dihapus dari daftar.' : 'This quest will be permanently removed from the list.'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: this.$t("quests.deleteBtn") || (this.$currentLocale === 'id' ? 'Hapus Misi' : 'Delete Quest'),
        cancelButtonText: this.$t("common.cancel") || (this.$currentLocale === 'id' ? 'Batal' : 'Cancel'),
      });
      if (!result.isConfirmed) return;

      try {
        await deleteReward(id);
        Swal.fire({
          icon: 'success',
          title: this.$t('quests.deleted') || (this.$currentLocale === 'id' ? 'Dihapus' : 'Deleted'),
          text: this.$t('quests.deletedDesc') || (this.$currentLocale === 'id' ? 'Misi telah berhasil dihapus.' : 'Quest has been successfully deleted.'),
          timer: 1500,
          showConfirmButton: false,
        });
        await this.fetchRewards();
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: this.$t('common.error') || (this.$currentLocale === 'id' ? 'Terjadi Kesalahan' : 'Error'),
          text: this.$t('quests.deleteFailed') || (this.$currentLocale === 'id' ? 'Gagal menghapus misi.' : 'Failed to delete quest.'),
        });
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
  },
};
</script>

<style scoped>
.btn-action-icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  transition: all 0.15s ease-in-out;
}
.btn-action-icon:hover {
  transform: translateY(-1px);
}
</style>
